<template>
  <form @submit.prevent="save">
    <b-message type="is-danger" v-if="errors.length > 0">
      <p v-for="error in errors">{{error}}</p>
    </b-message>
    <div class="columns is-multiline">
      <div class="column is-3">
        <b-field>
          <EmployeeSelect :initials.sync="form.resolution_maintainer__initials"
                          :label="`${$t('docflow.fields.resolution_maintainer')} *`"
                          :scope-all="true"
                          v-model="form.resolution_maintainer">
          </EmployeeSelect>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field>
          <EmployeeSelect :initials.sync="form.supervisor__initials"
                          :label="`${$t('docflow.fields.supervisor')} *`"
                          :disabled="isChangeRequest"
                          :scope-all="true"
                          v-model="form.supervisor">
          </EmployeeSelect>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field>
          <EmployeeSelect :initials.sync="form.resolution_approver__initials"
                          :label="`${$t('docflow.fields.resolution_approver')} *`"
                          :disabled="isChangeRequest"
                          :scope-all="true"
                          v-model="form.resolution_approver">
          </EmployeeSelect>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field>
          <form-datepicker :label="$t('docflow.fields.resolution_term_date')" :min-date="yesterday"
                           :max-date="maxTermDate"
                           v-model="form.resolution_term_date"></form-datepicker>
        </b-field>
      </div>
      <template v-for="(task, index) in form.resolution_tasks">
        <div class="column is-2">
          <EmployeeSelect :initials.sync="task.employee.initials"
                          :label="$t('documents.executor')"
                          :scope-all="isDocflowApprover"
                          v-model="task.employee.id">
          </EmployeeSelect>
        </div>
        <div class="column is-2">
          <b-field>
            <form-datepicker :label="$t('docflow.fields.term_date')" :min-date="yesterday"
                             :max-date="form.resolution_term_date"
                             v-model="task.term_date"></form-datepicker>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field>
            <form-select :label="`${$t('dashboard.itemTypes.task')}`"
                         v-model="task.type"
                         @change="template => task.description = template">
              <option v-for="item in resolutionTemplates" :value="item">
                {{ item | shortText(len=70) }}
              </option>
            </form-select>
          </b-field>
        </div>
        <div class="column is-5">
          <b-field>
            <form-input :label="$t('interface.description')"
                        :clearable="true"
                        v-model="task.description"></form-input>
          </b-field>
        </div>
      </template>
      <div class="column is-12" v-if="!isChangeRequest">
        <a @click="addTask" class="addTask">
          <b-icon icon="plus" size="is-small" class="icon"></b-icon>
        </a>
      </div>
      <div class="column is-4" v-if="!isChangeRequest">
        <b-field>
          <form-select :label="`${$t('docflow.resolutionTemplates')}`"
                       @change="template => form.resolution = `${form.resolution} ${template}.`.trim()">
            <option v-for="item in resolutionTemplates" :value="item">
              {{ item | shortText(len=70) }}
            </option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-12">
        <b-field>
          <form-textarea :label="$t('docflow.fields.resolution')"
                         :disabled="isChangeRequest"
                         v-model="form.resolution">
          </form-textarea>
        </b-field>
      </div>
      <div class="column is-12">
        <b-checkbox v-model="form.is_informational" :disabled="isChangeRequest">
          {{ $t('docflow.fields.is_informational') }}
        </b-checkbox>
      </div>
      <div class="column is-12">
        <div class="field is-grouped mt-10">
          <div class="control">
            <button type="submit" class="button is-primary">{{ $t('interface.save') }}</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click.prevent="cancel">{{ $t('interface.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {prepareDate} from '@/utils'
  import EmployeeSelect from '@/components/EmployeeSelect'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'
  import moment from 'moment'

  const defaultResolutionData = {
    resolution_approver: null,
    resolution_approver__initials: null,
    resolution_term_date: new Date(),
    resolution_maintainer: null,
    resolution_maintainer__initials: null,
    supervisor: null,
    supervisor__initials: null,
    resolution: '',
    is_informational: false,
    resolution_tasks: []
  }

  export default {
    props: ['resolutionData', 'editMode'],
    components: {
      EmployeeSelect,
      GovernmentAgencySelect
    },
    data() {
      return {
        form: this.resolutionData || {...defaultResolutionData},
        isEdit: this.editMode || false,
        errors: [],
        today: moment(new Date()).toDate(),
        tomorrow: moment(new Date()).add(1, 'days').toDate(),
        yesterday: moment(new Date()).add(-1, 'days').toDate(),
        maxTermDate: null
      }
    },
    methods: {
      validate() {
        this.errors = []
        if (this.form.resolution_approver === null) {
          this.errors.push(this.$t('interface.requiredFields'))
        }
        return this.errors.length === 0
      },
      save() {
        if (this.validate()) {
          this.$emit('save', {
            ...this.form,
            resolution_term_date: this.form.resolution_term_date ? prepareDate(this.form.resolution_term_date) : null,
            resolution_tasks: this.form.resolution_tasks.filter(task => task.employee.id).map(task => {
              return {
                ...task,
                term_date: prepareDate(task.term_date),
                employee: task.employee.id
              }
            })
          })
        }
      },
      cancel() {
        this.$router.go(-1)
      },
      addTask() {
        this.form.resolution_tasks.push({
          employee: {
            id: null,
            initials: null
          },
          type: null,
          description: '',
          term_date: new Date(this.document.term_date)
        })
      }
    },
    mounted() {
      this.$store.dispatch('Docflow/getRelations', {
        category: 'incoming', documentId: this.$route.params.documentId
      }).then(() => {
        if (!this.resolutionData) {
          this.form.resolution_term_date = new Date(this.document.term_date)
          this.form.resolution_tasks = this.employeeExecutors.map(executor => {
            return {
              employee: executor.employee,
              type: null,
              description: '',
              term_date: new Date(this.document.term_date)
            }
          })
          if (this.isDocflowApprover) {
            this.form.resolution_approver = this.account.employee.id
            this.form.resolution_approver__initials = this.account.employee.initials
          }
        }
        this.maxTermDate = new Date(this.document.term_date)
        this.form.is_informational = document.is_informational
      })
    },
    computed: {
      ...mapState('Docflow', ['types', 'deliveryTypes', 'document', 'resolutionTemplates', 'employeeExecutors']),
      ...mapGetters('Account', ['isDocflowApprover']),
      ...mapState('Account', ['account']),
      isChangeRequest() {
        return this.form.change_state ? this.form.change_state === 'approved' : false;
      }
    },
    watch: {
      resolutionData(document) {
        this.form = document
        let dataTasks = this.resolutionData.resolution_tasks.map(task => {
          return {
            pk: task.id,
            employee: task.employee,
            type: null,
            description: task.description,
            term_date: new Date(task.term_date)
          }
        })
        this.form.resolution_tasks = this.employeeExecutors.map(executor => {
          let task = dataTasks.find(task => task.employee.id === executor.employee.id)
          if (task) {
            return task
          } else {
            return {
              employee: executor.employee,
              type: null,
              description: '',
              term_date: new Date(this.document.term_date)
            }
          }
        })

        if (this.isChangeRequest) {
          this.form.resolution_tasks = this.form.resolution_tasks.filter(task => !!task.pk)
        }
        let uniqEmployees = this.form.resolution_tasks.map(task => task.employee.id)
        this.form.resolution_tasks = [
          ...this.form.resolution_tasks,
          ...dataTasks.filter(task => !uniqEmployees.includes(task.employee.id))
        ]
      },
      'form.resolution_maintainer'() {
        if (this.form.supervisor === null) {
          this.form.supervisor = this.form.resolution_maintainer
          this.form.supervisor__initials = this.form.resolution_maintainer__initials
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .templates {
    .button:not(:last-child) {
      margin-right: 10px;
    }
  }

  .delete-group {
    display: flex;
    justify-content: flex-end;

    & > :first-child {
      flex: 1;
    }

    a {
      margin-left: 10px;
      margin-top: 0;
    }
  }

  .addTask {
    border: 1.4px dashed #d1d1d1;
    border-radius: 5px;
    padding: 8px 0;
    display: block;
    text-align: center;

    .icon {
      vertical-align: middle;
      font-size: 18px;
      color: #bcbcbc;
      transition: none;
    }

    &:hover {
      border-color: #3498db;

      .icon {
        color: #3498db;
      }
    }
  }
</style>
